import {
	Image,
	Box,
	SimpleGrid,
	Title,
	UnstyledButton,
	Text,
	Flex,
	Button,
	Anchor,
} from '@mantine/core';
import { FC, useEffect, useRef } from 'react';
import { getFileSrc } from '@/utils/utilities';
import {
	checkProductModuleMatching,
	checkProductModuleSwapMatching,
} from '@/utils/creatorUtils';
import { useTypedDispatch, useTypedSelector } from '@/store/hooks';
import { ProjectRenderer } from '@/renderer/ProjectRenderer';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAddCreatorProductModuleMutation } from '@/hooks/useAddCreatorProductModuleMutation';
import { Project } from '@/api/ProjectApi';
import { Preset } from '@/api/PresetApi';
import { AttributeValue } from '@/api/AttributeValueApi';
import { creatorActions } from '@/store/slices/creatorSlice';
import CreatorProductModuleApi, {
	CreatorProductModule,
} from '@/api/CreatorProductModuleApi';
import ProductModuleApi, { ProductModule } from '@/api/ProductModuleApi';
import HelpIcon from '@/assets/icons/help.svg?react';
import { useContent } from '@/providers/ContentProvider';

const renderer = ProjectRenderer.getInstance();

const baseSlug = 'modulesList.';

interface Props {
	isSwap?: boolean;
}

export const ModulesList: FC<Props> = ({ isSwap = false }) => {
	const dispatch = useTypedDispatch();

	const { getContent } = useContent();

	const { options, selectedProductModuleCollection, project, preset } =
		useTypedSelector((state) => state.creator);

	const titleRef = useRef<HTMLDivElement>(null);

	const addCreatorProductModuleMutation = useAddCreatorProductModuleMutation();

	const updateCreatorProductModuleMutation = useMutation({
		mutationFn: CreatorProductModuleApi.swapProductModule,
		onSuccess: (creatorProductModule) => {
			dispatch(
				creatorActions.handleCreatorProductModuleSwap({
					creatorProductModuleId: creatorProductModule.id,
					productModule: creatorProductModule.productModule!,
				})
			);
		},
	});

	const productModulesQuery = useQuery({
		queryKey: [
			ProductModuleApi.queryKey,
			{
				sortBy: {
					key: 'listOrder',
					order: 'ASC',
				},
			},
			{
				'filter.productModuleCollection.id':
					selectedProductModuleCollection?.id,
			},
		],
		queryFn: ProductModuleApi.getAll,
		enabled: !!selectedProductModuleCollection,
	});

	const slotHostModule = (
		options.isPresetMode ? preset : project
	).creatorProductModules.find(
		(creProdMod) =>
			creProdMod.id === options.selectedSlot?.creatorProductModuleId
	);

	const handleAddModule = (productModule: ProductModule) => () => {
		let selectedAttributeValues = slotHostModule
			? slotHostModule.selectedAttributeValues!.map(
					(attributeValue) =>
						({
							id: attributeValue.id,
						}) as AttributeValue
				)
			: [];

		if (productModule.hasValuesOverride)
			selectedAttributeValues = selectedAttributeValues.filter(
				(attributeValue) =>
					productModule.attributeValueOverridesIds.includes(attributeValue.id)
			);

		const payload: Partial<CreatorProductModule> = {
			listOrder: 100,
			productModule: { id: productModule.id } as ProductModule,
			selectedAttributeValues,
		};

		if (options.isPresetMode) payload.preset = { id: preset.id } as Preset;
		else payload.project = { id: project.id } as Project;

		addCreatorProductModuleMutation.mutate(payload);
	};

	const handleSwapModule = (productModule: ProductModule) => () => {
		updateCreatorProductModuleMutation.mutate({
			id: options.swapDetails!.creatorProductModule!.id,
			productModule,
		});
	};

	const close = () => {
		renderer.deselectAll();
		dispatch(
			creatorActions.updateOptions({
				swapDetails: {
					slotTypes: [],
					creatorProductModule: null,
					modulesIds: [],
				},
			})
		);
	};

	const openProductModuleHelp = (productModule: ProductModule) => {
		dispatch(
			creatorActions.toogleHelpDrawer({
				helpDrawerOpened: true,
				helpDrawerData: {
					title: productModule.storeConfiguration.name,
					description: productModule.storeConfiguration.description,
					dimensions: productModule.storeConfiguration.dimensions,
					image: getFileSrc(productModule.image?.thumbnails[200]),
				},
			})
		);
	};

	useEffect(() => {
		if (!titleRef.current) return;
		titleRef.current.scrollIntoView();
	}, []);

	return (
		<Box>
			<Flex justify="space-between" align="center">
				<Title order={4} ref={titleRef}>
					{getContent(`${baseSlug}title`)}
				</Title>
				<Button onClick={close} size="sm" color="black">
					{getContent(`${baseSlug}close`)}
				</Button>
			</Flex>
			<SimpleGrid
				py="md"
				cols={{
					base: 2,
					sm: 3,
				}}
				spacing="xs"
			>
				{productModulesQuery.data
					?.filter((prodMod) =>
						isSwap
							? checkProductModuleSwapMatching(
									prodMod,
									options.swapDetails!,
									project.creatorProductModules
								)
							: checkProductModuleMatching(
									prodMod,
									options.selectedSlot!,
									slotHostModule!.productModule!
								)
					)
					.map((productModule) => {
						const hasHelpContent = Boolean(
							productModule.storeConfiguration.description ||
							productModule.storeConfiguration.dimensions
						);

						return (
							<UnstyledButton
								key={productModule.id}
								className="option-card"
								py="sm"
								px="xs"
								onClick={
									isSwap
										? handleSwapModule(productModule)
										: handleAddModule(productModule)
								}
							>
								<Flex className="image-container">
									{hasHelpContent && (
										<Anchor
											className="help-trigger"
											onClick={(e) => {
												e.stopPropagation();
												openProductModuleHelp(productModule);
											}}
										>
											<HelpIcon />
										</Anchor>
									)}
									<Image
										src={getFileSrc(productModule.image?.thumbnails[200])}
										w={75}
										h={75}
										mb="sm"
										loading="lazy"
									/>
								</Flex>
								<Text size="xs" fw="bold">
									{productModule.storeConfiguration.name}
								</Text>
							</UnstyledButton>
						);
					})}
			</SimpleGrid>
		</Box>
	);
};
